import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckSquare, FileCheck2, FileText, FolderKanban, Home, ReceiptText } from 'lucide-react';
import { useAuth } from '@/components/Auth/AuthContext';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { isCompanyAiEnabled } from '@/utils/companyAiAvailability';
import { hasRequiredRoleAccess } from '@/utils/swaRole';
import { bottomNavigationItems, filterNavigationItemsForUser, isNavigationItemActive } from './navigationItems';

const bottomNavIcons: Record<string, React.ElementType> = {
  '/': Home,
  '/projects': FolderKanban,
  '/quotations': FileText,
  '/invoices': ReceiptText,
  '/tasks': CheckSquare,
  '/certificates': FileCheck2,
};

const BottomNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isNarrowScreen = useIsNarrowScreen(640);
  const isVeryNarrowScreen = useIsNarrowScreen(380);
  const companyAiEnabled = isCompanyAiEnabled();
  const visibleItems = filterNavigationItemsForUser(bottomNavigationItems, (item) => (
    hasRequiredRoleAccess(user?.userRoles, user?.userDetails, item.requiredRoles || []) &&
    (item.path !== '/ai' || companyAiEnabled)
  ));

  if (!isNarrowScreen) {
    return null;
  }

  return (
    <nav
      aria-label="Primary"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.96)',
        borderTop: '1px solid rgba(211, 220, 232, 0.9)',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        padding: isNarrowScreen ? '6px 6px max(8px, env(safe-area-inset-bottom))' : '8px 14px max(10px, env(safe-area-inset-bottom))',
        zIndex: 1000,
        boxShadow: '0 -10px 24px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {visibleItems.map((item) => {
        const active = isNavigationItemActive(location.pathname, item);
        const Icon = bottomNavIcons[item.path] || Home;
        return (
          <button
            key={item.path}
            type="button"
            onClick={() => navigate(item.path)}
            aria-current={active ? 'page' : undefined}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              background: active ? 'rgba(34, 81, 180, 0.08)' : 'transparent',
              color: active ? '#2251b4' : '#667085',
              cursor: 'pointer',
              padding: isNarrowScreen ? '8px 3px 7px' : '9px 10px 8px',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              fontFamily: 'var(--font-family)',
              minHeight: isNarrowScreen ? '48px' : '52px',
            }}
            >
              <Icon size={isNarrowScreen ? (isVeryNarrowScreen ? 17 : 18) : 20} strokeWidth={active ? 2.4 : 2} aria-hidden="true" />
              <span
              style={{
                fontSize: isNarrowScreen ? (isVeryNarrowScreen ? '9px' : '10px') : '11px',
                fontWeight: active ? 800 : 600,
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                }}
              >
                {item.mobileLabel || item.desktopLabel}
              </span>
              <span
              style={{
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: active && !isVeryNarrowScreen ? '#2251b4' : 'transparent',
              }}
            />
          </button>
        );
      })}
    </nav>
  );
};

export default BottomNavigation;
